import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GameSummary, PalworldStatus, GameConfig, PlaytimeSummary } from "@black-wall/shared";

const BASE = "/api/gaming";

/** Registry of managed games (polls every 30s). */
export function useGames() {
  return useQuery<GameSummary[]>({
    queryKey: ["gaming", "games"],
    queryFn: async () => (await fetch(`${BASE}/games`)).json(),
    refetchInterval: 30_000,
  });
}

/** Live status of one game (game-specific shape; polls every 15s). */
export function useGameStatus(game: string) {
  return useQuery<PalworldStatus & { error?: string }>({
    queryKey: ["gaming", game, "status"],
    queryFn: async () => (await fetch(`${BASE}/${game}/status`)).json(),
    refetchInterval: 15_000,
    enabled: Boolean(game),
  });
}

/** Playtime leaderboard for a game (histórico acumulado en el VPS; polls every 60s). */
export function useGamePlaytime(game: string) {
  return useQuery<PlaytimeSummary & { error?: string }>({
    queryKey: ["gaming", game, "playtime"],
    queryFn: async () => (await fetch(`${BASE}/${game}/playtime`)).json(),
    refetchInterval: 60_000,
    enabled: Boolean(game),
  });
}

/** Fire an admin action against a game. */
export function useGameCommand(game: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { action: string; arg?: string }) => {
      const res = await fetch(`${BASE}/${game}/command`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return res.json() as Promise<{ ok?: boolean; output?: string; error?: string }>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gaming", game] }),
  });
}

/** Read a game's config file (only fetches when `enabled`). */
export function useGameConfig(game: string, enabled: boolean) {
  return useQuery<GameConfig & { error?: string }>({
    queryKey: ["gaming", game, "config"],
    queryFn: async () => (await fetch(`${BASE}/${game}/config`)).json(),
    enabled,
  });
}

/** Overwrite a game's config (server backs up the old one; optional restart). */
export function useSaveGameConfig(game: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { content: string; restart?: boolean }) => {
      const res = await fetch(`${BASE}/${game}/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return res.json() as Promise<{ ok?: boolean; restarted?: boolean; error?: string }>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gaming", game] }),
  });
}
